<template>
  <v-container fluid class="pa-4">
    <v-card elevation="2" rounded="lg">
      <v-card-title class="d-flex align-center justify-space-between flex-wrap ga-3">
        <div>
          <div class="text-h5 font-weight-bold">
            Campañas iniciadas sin planificar
          </div>

          <div class="text-body-2 text-medium-emphasis mt-1">
            Campañas activas con líneas sin fecha de reparto asignada.
          </div>
        </div>

        <div class="d-flex ga-2 align-center flex-wrap">
          <v-select
            v-model="filters.dies"
            :items="diesOptions"
            label="Revisar hasta"
            item-title="title"
            item-value="value"
            variant="outlined"
            density="compact"
            hide-details
            style="min-width: 190px"
          />

          <v-btn
            color="primary"
            variant="flat"
            :loading="loading"
            prepend-icon="mdi-refresh"
            @click="fetchData"
          >
            Actualizar
          </v-btn>
        </div>
      </v-card-title>

      <v-divider />

      <v-card-text>
        <v-alert
          v-if="errorMessage"
          type="error"
          variant="tonal"
          class="mb-4"
        >
          {{ errorMessage }}
        </v-alert>

        <v-alert
          type="info"
          variant="tonal"
          class="mb-4"
        >
          Revisamos campañas activas no planificadas hasta el día
          <strong>{{ formatDate(dataLimit) }}</strong>.
        </v-alert>

        <v-row class="mb-4">
          <v-col cols="12" md="3">
            <v-card variant="tonal" rounded="lg">
              <v-card-text>
                <div class="text-caption text-medium-emphasis">
                  Líneas
                </div>
                <div class="text-h5 font-weight-bold">
                  {{ items.length }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card variant="tonal" rounded="lg">
              <v-card-text>
                <div class="text-caption text-medium-emphasis">
                  Folletos
                </div>
                <div class="text-h5 font-weight-bold">
                  {{ totalFolletos.toLocaleString('es-ES') }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card
              variant="tonal"
              rounded="lg"
              :color="totalFueraFecha > 0 ? 'error' : undefined"
            >
              <v-card-text>
                <div class="text-caption text-medium-emphasis">
                  Fuera de fecha
                </div>
                <div class="text-h5 font-weight-bold">
                  {{ totalFueraFecha }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card
              variant="tonal"
              rounded="lg"
              :color="totalSinPlanificar > 0 ? 'warning' : undefined"
            >
              <v-card-text>
                <div class="text-caption text-medium-emphasis">
                  Sin planificar
                </div>
                <div class="text-h5 font-weight-bold">
                  {{ totalSinPlanificar }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-text-field
          v-model="search"
          label="Buscar por campaña, cliente, proveedor, zona, tienda o servicio"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          clearable
          hide-details
          class="mb-4"
        />

        <v-data-table
          :headers="headers"
          :items="items"
          :search="search"
          :loading="loading"
          item-value="id"
          density="compact"
          class="elevation-1 rounded-lg"
          :items-per-page="50"
        >
          <template #item.status_sort="{ item }">
            <v-chip
              :color="item.status_color"
              variant="tonal"
              size="small"
            >
              {{ item.status_label }}
            </v-chip>
          </template>

          <template #item.campaign_name="{ item }">
            <div class="font-weight-medium">
              {{ item.campaign_name }}
            </div>
          </template>

          <template #item.customer_name="{ item }">
            {{ item.customer_name }}
          </template>

          <template #item.partner_sort="{ item }">
            {{ item.partner_name || 'Sin proveedor' }}
          </template>

          <template #item.zone_name="{ item }">
            <div class="text-right">
              {{ item.zone_name }}
            </div>
          </template>

          <template #item.zone_city="{ item }">
            {{ item.zone_city }}
            <span v-if="item.zone_reference">
              , {{ item.zone_reference }}
            </span>
          </template>

          <template #item.dates_sort="{ item }">
            <div
              class="text-caption pa-1 rounded"
              :class="item.is_out_of_date ? 'bg-red-lighten-5 text-error' : 'bg-amber-lighten-5'"
            >
              <div>Inicio: {{ formatDate(item.start_date) }}</div>
              <div>Fin: {{ formatDate(item.end_date) }}</div>
            </div>
          </template>

          <template #item.quantity="{ item }">
            <div class="text-right font-weight-medium">
              {{ Number(item.quantity || 0).toLocaleString('es-ES') }}
            </div>
          </template>

          <template #item.shop="{ item }">
            {{ item.shop || '-' }}
          </template>

          <template #item.service_sort="{ item }">
            {{ item.service_type_name || '-' }}
          </template>

          <template #no-data>
            <div class="pa-6 text-center text-medium-emphasis">
              No hay campañas iniciadas sin planificar para el filtro seleccionado.
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import axios from 'axios'

const loading = ref(false)
const errorMessage = ref('')
const search = ref('')
const items = ref([])
const dataLimit = ref(null)

const filters = ref({
  dies: 1,
})

const diesOptions = [
  { title: 'Mañana', value: 1 },
  { title: 'Próximos 2 días', value: 2 },
  { title: 'Próximos 3 días', value: 3 },
  { title: 'Próximos 7 días', value: 7 },
  { title: 'Próximos 15 días', value: 15 },
  { title: 'Próximos 30 días', value: 30 },
]

const headers = [
  { title: 'Estado', key: 'status_sort', sortable: true },
  { title: 'Campaña', key: 'campaign_name', sortable: true },
  { title: 'Desc. campaña / Cliente', key: 'customer_name', sortable: true },
  { title: 'Proveedor', key: 'partner_sort', sortable: true },
  { title: 'Zona Traycco', key: 'zone_name', sortable: true },
  { title: 'Población', key: 'zone_city', sortable: true },
  { title: 'Fechas', key: 'dates_sort', sortable: true },
  { title: 'Folletos', key: 'quantity', sortable: true, align: 'end' },
  { title: 'Tienda', key: 'shop', sortable: true },
  { title: 'Servicio', key: 'service_sort', sortable: true },
]

const totalFolletos = computed(() => {
  return items.value.reduce((total, item) => total + Number(item.quantity || 0), 0)
})

const totalFueraFecha = computed(() => {
  return items.value.filter(item => item.is_out_of_date).length
})

const totalSinPlanificar = computed(() => {
  return items.value.filter(item => !item.is_out_of_date).length
})

async function fetchData() {
  loading.value = true
  errorMessage.value = ''

  try {
    const response = await axios.get('/api/admin/produccion/campanyas-iniciadas-sin-planificar', {
      params: {
        dies: filters.value.dies,
      },
    })

    dataLimit.value = response.data?.data_limit || null

    items.value = (response.data?.data || []).map(item => ({
      ...item,

      partner_sort: item.partner_name || '',
      service_sort: item.service_type_name || '',
      status_sort: item.is_out_of_date ? '1_fuera_fecha' : '2_sin_planificar',
      dates_sort: item.end_date || item.start_date || '',

      searchable_text: [
        item.campaign_name,
        item.customer_name,
        item.partner_name,
        item.zone_name,
        item.zone_city,
        item.zone_reference,
        item.shop,
        item.service_type_name,
        item.status_label,
      ].filter(Boolean).join(' '),
    }))
  } catch (error) {
    console.error(error)

    errorMessage.value =
      error.response?.data?.message ||
      'No se ha podido cargar el listado de campañas iniciadas sin planificar.'
  } finally {
    loading.value = false
  }
}

function formatDate(date) {
  if (!date) return '-'

  const cleanDate = String(date).slice(0, 10)
  const parts = cleanDate.split('-')

  if (parts.length !== 3) {
    return date
  }

  return `${parts[2]}/${parts[1]}/${parts[0]}`
}

onMounted(() => {
  fetchData()
})
</script>